import { loginSchema } from '@planuze/admin-contracts';
import { Button, glassLogoUrl, InlineAlert, Input, Label, Logo, toast } from '@planuze/ui';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { type CommonContent, useCommonContent } from '../content/sections/common.js';
import { ApiError, login } from '../lib/api-client.js';
import { useTheme } from './theme-provider.js';

/* Mapeia o erro para mensagem localizada. Distingue 429 de credenciais inválidas: sem isso o
   operador em rate-limit veria "credenciais inválidas" e ficaria tentando. */
function messageForError(err: unknown, copy: CommonContent['loginForm']): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'rate_limited':
      case 'account_locked':
        return copy.rateLimited;
      case 'network':
        return copy.networkFailure;
      default:
        return copy.invalidCredentials;
    }
  }
  return copy.unexpectedError;
}

interface LoginFormProps {
  /** Disparado após login bem-sucedido (a rota navega para o destino protegido). */
  readonly onSuccess: () => void;
}

/* Tela de acesso do backoffice (ADR-0700): split-panel — painel de marca (navy + accent) à
   esquerda e o formulário à direita; em telas pequenas o painel some e a marca aparece acima do
   form. Logo prominente (antes o login era um Card genérico sem marca). */
export function LoginForm({ onSuccess }: LoginFormProps) {
  const { resolved } = useTheme();
  const copy = useCommonContent().loginForm;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(copy.validationError);
      return;
    }
    setSubmitting(true);
    try {
      await login(parsed.data);
      toast.success(copy.signedIn);
      onSuccess();
    } catch (err) {
      setError(messageForError(err, copy));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      {/* Painel de marca — imagem "glass" da Planuze em full-bleed com overlay navy p/ legibilidade.
          Oculto em telas pequenas. */}
      <aside className="relative hidden overflow-hidden bg-primary lg:block">
        <img src={glassLogoUrl} alt="" className="absolute inset-0 size-full object-cover" />
        {/* Overlays navy — véu base LEVE (foto aparece mais) + scrims só onde há texto/logo:
            forte embaixo (texto legível) e sutil no topo (logo). */}
        <div aria-hidden="true" className="absolute inset-0 bg-primary/25" />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-primary via-primary/40 to-transparent"
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-primary/45 to-transparent"
        />
        <div className="relative flex h-full flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-2">
            <Logo variant="white" className="h-7 w-auto" />
            <span className="rounded bg-primary-foreground/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground/90">
              {copy.adminChip}
            </span>
          </div>
          <div className="max-w-md space-y-3">
            <h1 className="text-3xl font-semibold leading-tight tracking-tight">
              {copy.heroTitle}
            </h1>
            <p className="max-w-sm text-sm leading-relaxed text-primary-foreground/75">
              {copy.heroDescription}
            </p>
            <p className="pt-2 text-xs text-primary-foreground/55">{copy.restrictedAccess}</p>
          </div>
        </div>
      </aside>

      {/* Painel do formulário. */}
      <main className="flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm space-y-8">
          {/* Marca no topo apenas em telas pequenas (painel oculto). */}
          <div className="flex items-center gap-2 lg:hidden">
            <Logo variant={resolved === 'dark' ? 'white' : 'black'} className="h-7 w-auto" />
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {copy.adminChip}
            </span>
          </div>

          <div className="space-y-1.5">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">
              {copy.heading}
            </h2>
            <p className="text-sm text-muted-foreground">{copy.subheading}</p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4"
            aria-label={copy.formAriaLabel}
            noValidate
          >
            <div className="space-y-1.5">
              <Label htmlFor="email">{copy.emailLabel}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder={copy.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{copy.passwordLabel}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={
                    showPassword ? copy.hidePasswordAriaLabel : copy.showPasswordAriaLabel
                  }
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOffIcon className="size-4" aria-hidden="true" />
                  ) : (
                    <EyeIcon className="size-4" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
            {error !== null && <InlineAlert variant="destructive">{error}</InlineAlert>}
            <Button type="submit" className="w-full" loading={submitting}>
              {copy.submit}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
